import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import logger from '@widgets/logger';
import { useCurrentEditor } from '@tiptap/react';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import InboxTemplateMenu from '@features/InboxTemplateMenu';
import CouponsMenu from '@features/CouponsMenu';
import Button from '@components/Button';
import EmojiPicker from '@components/EmojiPicker';
import TextEditor, { EDITOR_TYPES } from '@components/TextEditor';
import { AITaskType, AI_TASK_TYPE } from '@constants/ai-assistant';
import { validateFileSize } from '@helpers/upload-file';
import { parseTipTapJSONToPlainText } from '@helpers/text-editor';
import { handleApiError } from '@helpers/api';
import { useTranslation, useThrottle, useDebouncedCallback } from '@hooks';
import { alertActions } from '@actions';
import { inboxService } from '@services';
import { MB } from '@constants/file';
import { ConversationStatus } from '@constants/conversations';
import { SendIcon, AttachIcon, SmileIcon } from 'assets/images/icons';
import SurveysMenu from './SurveysMenu';

const StyledTextEditor = styled(TextEditor)({
    paddingBottom: ' 0 !important',
    '& .ProseMirror': {
        maxHeight: '160px !important',
        minHeight: 'auto !important',
    },
    '& .editor-container': {
        minHeight: '78.5px !important',
        paddingBottom: '46px !important',
        '&:focus-within': {
            padding: '15.5px 13px 45px 13px !important',
        },
    },
    '& .editor-toolbar': {
        bottom: '16.5px !important',
    },
    '& em-emoji-picker': {
        width: '100% !important',
        maxWidth: '100% !important',
    },
    '@media (max-width: 1024px)': {
        '& .emoji-picker-button': {
            display: 'none',
        },
    },
});

const StyledButton = styled(Button)({
    height: 24,
    minWidth: 'auto',
    '& .MuiButton-endIcon': {
        transform: 'rotate(90deg)',
        margin: 0,
        '& svg path': {
            fillOpacity: 1,
        },
    },
});

const SendButton = ({ disabled, onClick }) => {
    const { editor } = useCurrentEditor();

    const handleClick = async () => {
        try {
            await onClick();
            editor?.commands.clearContent();
        } catch (error) {
            logger.error(error);
        }
    };
    return <StyledButton variant="contained" disabled={disabled} onClick={handleClick} endIcon={<SendIcon />} />;
};

const EmojiToolbarButton = ({ disabled, anchorRef, onEmitStartTyping, onEmitStopTyping }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { editor } = useCurrentEditor();

    const handleOpen = () => setAnchorEl(anchorRef.current);

    const handleClose = () => setAnchorEl(null);

    const handleEmojiSelect = emoji => {
        editor
            ?.chain()
            .focus()
            .insertContent(emoji?.native || '')
            .run();
        onEmitStartTyping();
        onEmitStopTyping();
    };

    return (
        <EmojiPicker
            element={
                <IconButton
                    className="emoji-picker-button"
                    size="small"
                    color="inherit"
                    onClick={handleOpen}
                    disabled={disabled}
                    sx={{ height: 18, width: 18, padding: '2px !important' }}
                >
                    <SmileIcon />
                </IconButton>
            }
            anchorEl={anchorEl}
            onClose={handleClose}
            onEmojiSelect={handleEmojiSelect}
            dynamicWidth
        />
    );
};

const InboxInput = ({ activeConversation, surveysEnabled, couponsEnabled }) => {
    const [message, setMessage] = useState('');
    const [isSendingMessage, setIsSendingMessage] = useState(false);

    const containerRef = useRef(null);
    const isTypingRef = useRef(false);
    const attachInputRef = useRef(null);

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const onEmitInstantTyping = (isTyping = true) => {
        inboxService.sendTypingStatus(activeConversation?.id, isTyping);
    };

    const [onEmitStartTyping] = useThrottle(() => {
        if (!isTypingRef.current) {
            isTypingRef.current = true;
            onEmitInstantTyping(true);
        }
    }, 1500);

    const onEmitStopTyping = useDebouncedCallback(() => {
        if (isTypingRef.current) {
            isTypingRef.current = false;
            onEmitInstantTyping(false);
        }
    }, 2000);

    useEffect(() => {
        if (activeConversation?.id) {
            setMessage('');
            if (isTypingRef.current) {
                isTypingRef.current = false;
                onEmitInstantTyping(false);
            }
        }
    }, [activeConversation?.id]);

    useEffect(() => {
        return () => {
            if (isTypingRef.current) {
                onEmitInstantTyping(false);
            }
        };
    }, []);

    const isConversationOpen = activeConversation?.status === ConversationStatus.Open;
    const isSendDisabled = !activeConversation || !isConversationOpen || isSendingMessage;

    const handleMessageChange = text => {
        setMessage(text);

        if (activeConversation && !isSendDisabled) {
            onEmitStartTyping();
            onEmitStopTyping();
        }
    };

    const onClickSendMessage = async () => {
        try {
            setIsSendingMessage(true);
            if (isTypingRef.current) {
                isTypingRef.current = false;
                onEmitInstantTyping(false);
            }
            await inboxService.sendMessage(activeConversation?.id, parseTipTapJSONToPlainText(message));
            setMessage('');
        } catch (error) {
            handleApiError(error);
            throw error;
        } finally {
            setIsSendingMessage(false);
        }
    };

    const onUploadFile = async e => {
        if (!e.target.files[0]) return;

        const { error, metric, size } = validateFileSize(e.target.files[0], 5 * MB);
        if (error) {
            dispatch(alertActions.error(t('apiErrors.file_size_too_large', { size, metric })));
            return;
        }
        try {
            setIsSendingMessage(true);
            const data = new FormData();
            data.append('file', e.target.files[0]);
            data.append('fileName', e.target.files[0].name);
            data.append('conversationId', activeConversation?.id);
            if (isTypingRef.current) {
                isTypingRef.current = false;
                onEmitInstantTyping(false);
            }
            await inboxService.uploadFile(data);
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsSendingMessage(false);
            e.target.value = '';
        }
    };

    const onClickAttach = () => {
        if (!isSendDisabled) {
            attachInputRef.current?.click();
        }
    };

    return (
        <StyledTextEditor
            containerRef={containerRef}
            editorType={EDITOR_TYPES.INBOX}
            initialValue={''}
            onChange={handleMessageChange}
            editable={!isSendDisabled}
            aiAssistant={{
                additionalOptions: [
                    {
                        text: t('AIAssistant.writeResponse'),
                        type: AITaskType.GenerateInboxMessage,
                        taskType: AI_TASK_TYPE.CONTEXTUAL,
                        conversationId: activeConversation?.id,
                    },
                ],
            }}
            containerProps={{
                onKeyDown: async (e, editor) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        if (!isSendDisabled && message) {
                            await onClickSendMessage();
                            editor?.commands.clearContent();
                        }
                    }
                },
            }}
            placeholder={t('InboxPage.messageLabel')}
            toolbar={
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'flex-end',
                    }}
                >
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <InboxTemplateMenu
                            disabled={isSendDisabled}
                            content={message}
                            setContent={text => {
                                setMessage(text);
                                onEmitStartTyping();
                                onEmitStopTyping();
                            }}
                            tags={{
                                firstName: activeConversation?.firstName,
                                lastName: activeConversation?.lastName,
                                locations: activeConversation?.locations,
                            }}
                        />
                        {surveysEnabled && (
                            <SurveysMenu
                                disabled={isSendDisabled}
                                setMessage={text => {
                                    setMessage(text);
                                    onEmitStartTyping();
                                    onEmitStopTyping();
                                }}
                                activeConversation={activeConversation}
                            />
                        )}
                        {couponsEnabled && (
                            <CouponsMenu
                                disabled={isSendDisabled}
                                message={message}
                                setMessage={text => {
                                    setMessage(text);
                                    onEmitStartTyping();
                                    onEmitStopTyping();
                                }}
                                context={{
                                    locationId: activeConversation?.locations[0],
                                    firstName: activeConversation?.firstName,
                                    lastName: activeConversation?.lastName,
                                }}
                            />
                        )}
                        <EmojiToolbarButton
                            anchorRef={containerRef}
                            disabled={isSendDisabled}
                            onEmitStartTyping={onEmitStartTyping}
                            onEmitStopTyping={onEmitStopTyping}
                        />

                        <IconButton
                            size="small"
                            color="inherit"
                            onClick={onClickAttach}
                            sx={{ height: 18, width: 18, padding: '2px !important' }}
                        >
                            <div style={{ display: 'flex' }}>
                                <input ref={attachInputRef} hidden accept="*/*" type="file" onChange={onUploadFile} />
                                <AttachIcon style={{ width: 13 }} />
                            </div>
                        </IconButton>
                    </div>
                    <SendButton disabled={isSendDisabled || !message} onClick={onClickSendMessage} />
                </div>
            }
        />
    );
};

export default InboxInput;
